import React, { useState } from 'react';
import { Layout, Typography, Button, Drawer } from 'antd';
import { MessageOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';

const { Text: AntText } = Typography;
import ChatList from './ChatList';
import ChatInterface from './ChatInterface';

const { Content, Sider } = Layout;
const { Title } = Typography;

function ChatPage() {
    const [selectedLead, setSelectedLead] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [drawerVisible, setDrawerVisible] = useState(false);

    React.useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleSelectLead = (lead) => {
        setSelectedLead(lead);
        if (isMobile) {
            setDrawerVisible(false);
        }
    };

    const handleCloseChat = () => {
        setSelectedLead(null);
    };

    const handleBackToList = () => {
        setSelectedLead(null);
        if (isMobile) {
            setDrawerVisible(true);
        }
    };

    const handleNewConversation = () => {
        // Navigate to get started or create new lead
        window.location.href = '/get-started';
    };

    return (
        <div className="h-screen bg-[#FAF8F5]">
            <Layout className="h-full">
                {/* Desktop Layout */}
                {!isMobile ? (
                    <>
                        <Sider width={380} className="bg-[#FAF8F5] border-r border-[#EAE7E3] relative">
                            <ChatList 
                                onSelectLead={handleSelectLead}
                                selectedLeadId={selectedLead?.id}
                            />
                            {/* New Conversation Button - Fixed at bottom */}
                            <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t border-[#EAE7E3]">
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={handleNewConversation}
                                    block
                                    className="h-12 rounded-lg font-semibold"
                                    style={{
                                        background: '#C97D60',
                                        border: 'none',
                                        boxShadow: '0 2px 8px rgba(201, 125, 96, 0.3)'
                                    }}
                                >
                                    New Conversation
                                </Button>
                            </div>
                        </Sider>
                        <Content className="flex-1 bg-white">
                            {selectedLead ? (
                                <ChatInterface 
                                    leadId={selectedLead.id}
                                    onClose={handleCloseChat}
                                />
                            ) : (
                                <div className="h-full flex items-center justify-center bg-[#FAF8F5]">
                                    <div className="text-center p-8">
                                        <MessageOutlined className="text-6xl text-[#C97D60] mb-6" />
                                        <Title level={3} className="!text-[#2C2C2C] !mb-2" style={{ fontFamily: 'var(--font-serif)' }}>
                                            Welcome to Messages
                                        </Title>
                                        <AntText type="secondary" className="text-lg text-[#4A4A4A]">
                                            Select a conversation to start chatting
                                        </AntText>
                                    </div>
                                </div>
                            )}
                        </Content>
                    </>
                ) : (
                    /* Mobile Layout */
                    <>
                        {!selectedLead ? (
                            <Content className="h-full bg-[#FAF8F5] relative">
                                <div className="p-4 sm:p-6">
                                    <div className="flex items-center mb-4">
                                        <MessageOutlined className="text-[#C97D60] text-xl mr-3" />
                                        <Title level={2} className="!mb-0 !text-[#2C2C2C] font-bold" style={{ fontFamily: 'var(--font-serif)' }}>
                                            Messages
                                        </Title>
                                    </div>
                                    <div className="text-center py-16">
                                        <MessageOutlined className="text-5xl text-[#C97D60] mb-6" />
                                        <Title level={4} className="!text-[#2C2C2C] !mb-2" style={{ fontFamily: 'var(--font-serif)' }}>
                                            Welcome to Messages
                                        </Title>
                                        <AntText type="secondary" className="text-base text-[#4A4A4A]">
                                            Select a conversation to start chatting
                                        </AntText>
                                    </div>
                                </div>
                                {/* New Conversation Button - Fixed at bottom */}
                                <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-[#EAE7E3]">
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={handleNewConversation}
                                        block
                                        className="h-12 rounded-lg font-semibold"
                                        style={{
                                            background: '#C97D60',
                                            border: 'none',
                                            boxShadow: '0 2px 8px rgba(201, 125, 96, 0.3)'
                                        }}
                                    >
                                        New Conversation
                                    </Button>
                                </div>
                            </Content>
                        ) : (
                            <Content className="h-full">
                                <div className="bg-white border-b border-[#EAE7E3] p-4 flex items-center">
                                    <Button 
                                        type="text" 
                                        icon={<CloseOutlined />}
                                        onClick={handleBackToList}
                                        className="mr-3 text-[#4A4A4A] hover:bg-[#F5F3F0] rounded-lg"
                                    />
                                    <Title level={5} className="!mb-0 flex-1 !text-[#2C2C2C]" style={{ fontFamily: 'var(--font-serif)' }}>
                                        {selectedLead.user?.name || selectedLead.vendor?.business_name}
                                    </Title>
                                </div>
                                <ChatInterface 
                                    leadId={selectedLead.id}
                                    onClose={handleCloseChat}
                                />
                            </Content>
                        )}

                        <Drawer
                            title={
                                <div className="flex items-center">
                                    <MessageOutlined className="text-[#C97D60] text-xl mr-3" />
                                    <span style={{ fontFamily: 'var(--font-serif)' }}>Messages</span>
                                </div>
                            }
                            placement="left"
                            onClose={() => setDrawerVisible(false)}
                            open={drawerVisible}
                            width="100%"
                            className="mobile-drawer"
                            styles={{
                                body: { padding: 0, background: '#FAF8F5' }
                            }}
                        >
                            <div className="h-full flex flex-col">
                                <ChatList 
                                    onSelectLead={handleSelectLead}
                                    selectedLeadId={selectedLead?.id}
                                />
                                {/* New Conversation Button - Fixed at bottom */}
                                <div className="p-4 bg-white border-t border-[#EAE7E3]">
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => { handleNewConversation(); setDrawerVisible(false); }}
                                        block
                                        className="h-12 rounded-lg font-semibold"
                                        style={{
                                            background: '#C97D60',
                                            border: 'none',
                                            boxShadow: '0 2px 8px rgba(201, 125, 96, 0.3)'
                                        }}
                                    >
                                        New Conversation
                                    </Button>
                                </div>
                            </div>
                        </Drawer>
                    </>
                )}
            </Layout>
        </div>
    );
}

export default ChatPage;
